import { saveMany } from '../store/items';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import axios from 'axios';

const MAX_CONCURRENT_REQUESTS = 4;
const requestQueue = [];
let inFlight = 0;
const retryAfterById = new Map();
const pendingById = new Map();
const queuedIds = new Set();
const queuedResolversById = new Map();
let flushScheduled = false;
const isValidObjectId = id => typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);
const MISSING_RETRY_DELAY_MS = 30000;
const ERROR_RETRY_DELAY_MS = 5000;
const BATCH_COALESCE_MS = 120;

const enqueueRequest = requestFn =>
  new Promise((resolve, reject) => {
    requestQueue.push({ requestFn, resolve, reject });
    const runNext = () => {
      if (inFlight >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
        return;
      }
      const next = requestQueue.shift();
      if (!next) {
        return;
      }
      inFlight += 1;
      Promise.resolve()
        .then(next.requestFn)
        .then(result => next.resolve(result))
        .catch(error => next.reject(error))
        .finally(() => {
          inFlight -= 1;
          runNext();
        });
    };
    runNext();
  });

const flushQueuedItemFetches = async () => {
  flushScheduled = false;
  if (queuedIds.size === 0) {
    return;
  }
  const ids = Array.from(queuedIds);
  queuedIds.clear();

  try {
    const res = await enqueueRequest(() =>
      axios.post(`${process.env.REACT_APP_API_URL}/items/batch`, { ids }),
    );
    const returnedItems = Array.isArray(res?.data) ? res.data : [];
    const returnedById = new Map(
      returnedItems.filter(item => item?._id).map(item => [item._id, item]),
    );

    ids.forEach(id => {
      const handlers = queuedResolversById.get(id) || [];
      queuedResolversById.delete(id);
      const item = returnedById.get(id) || null;
      if (item) {
        retryAfterById.delete(id);
      } else {
        retryAfterById.set(id, Date.now() + MISSING_RETRY_DELAY_MS);
      }
      pendingById.delete(id);
      handlers.forEach(({ resolve }) => resolve(item));
    });
  } catch (error) {
    ids.forEach(id => {
      const handlers = queuedResolversById.get(id) || [];
      queuedResolversById.delete(id);
      retryAfterById.set(id, Date.now() + ERROR_RETRY_DELAY_MS);
      pendingById.delete(id);
      handlers.forEach(({ reject }) => reject(error));
    });
  }
};

const scheduleFlushQueuedItemFetches = () => {
  if (flushScheduled) {
    return;
  }
  flushScheduled = true;
  setTimeout(() => {
    flushQueuedItemFetches();
  }, BATCH_COALESCE_MS);
};

const fetchItemById = id => {
  if (pendingById.has(id)) {
    return pendingById.get(id);
  }

  const requestPromise = new Promise((resolve, reject) => {
    if (!queuedResolversById.has(id)) {
      queuedResolversById.set(id, []);
    }
    queuedResolversById.get(id).push({ resolve, reject });
    queuedIds.add(id);
    scheduleFlushQueuedItemFetches();
  });
  pendingById.set(id, requestPromise);
  return requestPromise;
};

const useItems = itemIds => {
  if (itemIds === undefined || itemIds === null) {
    itemIds = [];
  }

  const dispatch = useDispatch();
  const ids = useMemo(
    () => (Array.isArray(itemIds) ? itemIds.filter(Boolean) : []),
    [itemIds],
  );
  const stableIdsKey = useMemo(() => ids.join(','), [ids]);
  const selectedItems = useSelector(state => {
    const byId = {};
    ids.forEach(id => {
      if (state.items[id]) {
        byId[id] = state.items[id];
      }
    });
    return byId;
  }, shallowEqual);

  useEffect(() => {
    const fetchItems = async () => {
      const now = Date.now();
      const uncachedIds = ids?.filter(id => {
        if (!isValidObjectId(id)) {
          return false;
        }
        const existing = selectedItems[id];
        if (existing && existing.__partial !== true) {
          return false;
        }
        const retryAfter = retryAfterById.get(id) || 0;
        if (retryAfter > now) {
          return false;
        }
        return true;
      });
      const uniqueIds = Array.from(new Set(uncachedIds || []));

      if (uniqueIds.length > 0) {
        const fetchedItems = await Promise.all(
          uniqueIds.map(id => fetchItemById(id).catch(() => null)),
        );
        const nextItems = fetchedItems.filter(item => {
          if (!item?._id) {
            return false;
          }
          const existing = selectedItems[item._id];
          return !existing || existing.__partial === true;
        });
        if (nextItems.length > 0) {
          dispatch(saveMany({ items: nextItems, log: 'from useItems' }));
        }
      }
    };

    fetchItems();
  }, [stableIdsKey, ids, selectedItems, dispatch]);

  return useMemo(() => ids?.map(id => selectedItems[id]), [ids, selectedItems]);
};

export default useItems;
